// Step-by-step progress view for an in-flight strategy deployment.
//
// Mounted on the Overview page. Polls the active deployment for the
// selected strategy every 2 seconds via useActiveDeployment(). Renders
// nothing if there's no active deployment (so it disappears once the
// strategy is live and operating normally).
//
// During dry-run mode the panel shows the planned actions + amounts
// without any txs broadcasting. When all steps complete (status='completed'
// + mode='dry_run') the panel surfaces an "Execute deployment" button.
// Click → flips to execute mode + bot replays all 6 steps with real txs.
//
// Page reload safe: state lives in DB. Reload → component re-mounts →
// polling resumes from current step.
import { useState as useReactState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AlertCircle, CheckCircle2, Circle, ExternalLink, Loader2, Pencil, Play, RefreshCw, X, XCircle } from 'lucide-react';
import { useActiveDeployment } from '../hooks/useApi';
import {
  deploymentsApi,
  type DeploymentStepLogEntry,
  type StrategyDeployment,
} from '../lib/api';
import { Button } from './ui/button';
import { SectionHeader } from './ui/section-header';
import { cn, formatCurrency } from '../lib/utils';

const STEP_LABELS: Record<string, string> = {
  verify_funds: 'Verify wallet USDC',
  compute_allocation: 'Compute allocation',
  verify_hl_balance: 'Verify HL balance',
  swap_usdc_to_weth: 'Swap USDC → WETH',
  mint_lp: 'Mint LP position',
  stake_lp: 'Stake LP in gauge',
};

const STEP_ORDER = [
  'verify_funds',
  'compute_allocation',
  'verify_hl_balance',
  'swap_usdc_to_weth',
  'mint_lp',
  'stake_lp',
];

interface Props {
  strategyId: number | null;
}

export function DeploymentProgressPanel({ strategyId }: Props) {
  const { data: deployment, isLoading } = useActiveDeployment(strategyId);
  if (!strategyId || isLoading) return null;
  if (!deployment) return null;

  return <DeploymentProgressInner deployment={deployment} />;
}

function DeploymentProgressInner({ deployment }: { deployment: StrategyDeployment }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useReactState<'execute' | 'retry' | 'reset' | null>(null);
  const [toastError, setToastError] = useReactState<string | null>(null);
  /** Inline budget-edit form: null = collapsed, string = current input value. */
  const [editBudget, setEditBudget] = useReactState<string | null>(null);

  const isDryRun = deployment.mode === 'dry_run';
  const isCompleted = deployment.status === 'completed';
  const isFailed = deployment.status === 'failed';
  const isRunning =
    deployment.status === 'running' || deployment.status === 'pending';

  // Map step_name → most recent log entry. Latest wins so retries
  // override the earlier failed attempt visually.
  const stepEntries = new Map<string, DeploymentStepLogEntry>();
  for (const entry of deployment.stepLog) {
    if (entry.name.startsWith('__')) continue; // skip transition markers
    stepEntries.set(entry.name, entry);
  }

  async function onExecute() {
    setBusy('execute');
    setToastError(null);
    try {
      await deploymentsApi.execute(deployment.id);
      await qc.invalidateQueries({ queryKey: ['deployment-active', deployment.strategyId] });
    } catch (err) {
      setToastError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  async function onRetry() {
    setBusy('retry');
    setToastError(null);
    try {
      await deploymentsApi.retry(deployment.id);
      await qc.invalidateQueries({ queryKey: ['deployment-active', deployment.strategyId] });
    } catch (err) {
      setToastError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  async function onResetBudget() {
    if (editBudget === null) return;
    const trimmed = editBudget.trim();
    const parsed = trimmed === '' ? null : Number(trimmed);
    if (parsed !== null && (!Number.isFinite(parsed) || parsed <= 0)) {
      setToastError('Budget must be a positive number.');
      return;
    }
    setBusy('reset');
    setToastError(null);
    try {
      await deploymentsApi.reset(
        deployment.id,
        parsed !== null && parsed !== deployment.budgetUsdc
          ? { budgetUsdc: parsed }
          : undefined,
      );
      await qc.invalidateQueries({ queryKey: ['deployment-active', deployment.strategyId] });
      await qc.invalidateQueries({ queryKey: ['state'] });
      setEditBudget(null);
    } catch (err) {
      setToastError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="rounded-lg border border-border bg-surface p-6 space-y-4">
      <SectionHeader
        title={
          isDryRun
            ? 'Deployment dry-run in progress'
            : 'Deployment in progress'
        }
        subtitle={
          isDryRun
            ? 'No funds committed yet. Preview the planned actions, then click Execute when ready.'
            : 'Real transactions broadcasting. Each step is recorded on-chain.'
        }
        size="sm"
        actions={
          <div className="flex items-center gap-2">
            {/* Edit budget only valid while in dry-run mode — once execute
                fires, real funds are committed and editing budget would
                desync the on-chain state from the deployment row. */}
            {isDryRun && editBudget === null && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEditBudget(String(deployment.budgetUsdc))}
                disabled={busy !== null}
              >
                <Pencil className="h-3 w-3" /> Edit budget
              </Button>
            )}
            <ModeBadge mode={deployment.mode} status={deployment.status} />
          </div>
        }
      />

      {/* Inline budget edit form */}
      {editBudget !== null && (
        <div className="flex items-center gap-2 p-3 rounded-md border border-primary/40 bg-primary/5">
          <label className="text-xs text-foreground font-medium whitespace-nowrap">
            New budget (USDC):
          </label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={editBudget}
            onChange={(e) => setEditBudget(e.target.value)}
            className="flex-1 px-2 py-1 text-sm rounded-md border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
            autoFocus
            disabled={busy !== null}
          />
          <Button
            size="sm"
            onClick={onResetBudget}
            disabled={busy !== null}
          >
            {busy === 'reset' ? (
              <>
                <Loader2 className="h-3 w-3 animate-spin" /> Saving…
              </>
            ) : (
              'Save & rerun dry-run'
            )}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setEditBudget(null);
              setToastError(null);
            }}
            disabled={busy !== null}
          >
            <X className="h-3 w-3" /> Cancel
          </Button>
        </div>
      )}

      {/* Allocation breakdown */}
      {deployment.allocationLpUsdc !== null && (
        <div className="grid grid-cols-3 gap-3 text-xs">
          <AllocationCard
            label="Budget"
            value={formatCurrency(deployment.budgetUsdc)}
          />
          <AllocationCard
            label="LP allocation"
            value={formatCurrency(deployment.allocationLpUsdc)}
          />
          <AllocationCard
            label="HL allocation"
            value={formatCurrency(deployment.allocationHlUsdc ?? 0)}
          />
        </div>
      )}

      {/* Step list */}
      <ol className="space-y-2">
        {STEP_ORDER.map((name, idx) => {
          const entry = stepEntries.get(name);
          const isCurrent = deployment.currentStep === idx && isRunning;
          return (
            <StepRow
              key={name}
              idx={idx}
              name={name}
              label={STEP_LABELS[name] ?? name}
              entry={entry}
              isCurrent={isCurrent}
            />
          );
        })}
      </ol>

      {/* Status-specific footer */}
      {isCompleted && isDryRun && (
        <div className="flex items-center justify-between p-3 rounded-md border border-primary/40 bg-primary/5">
          <div className="text-xs text-foreground">
            <p className="font-medium">Dry-run plan ready.</p>
            <p className="text-muted-foreground mt-0.5">
              Review the steps above. Click Execute to commit funds (this will
              broadcast real transactions).
            </p>
          </div>
          <Button
            onClick={onExecute}
            disabled={busy !== null}
            size="md"
          >
            {busy === 'execute' ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Executing…
              </>
            ) : (
              <>
                <Play className="h-4 w-4" /> Execute deployment
              </>
            )}
          </Button>
        </div>
      )}
      {isFailed && (
        <div className="flex items-center justify-between p-3 rounded-md border border-negative/40 bg-negative/5">
          <div className="text-xs text-foreground max-w-2xl">
            <p className="font-medium text-negative">Deployment failed</p>
            <p className="text-muted-foreground mt-0.5 font-mono break-all">
              {deployment.errorMessage}
            </p>
          </div>
          <Button
            onClick={onRetry}
            disabled={busy !== null}
            size="md"
            variant="outline"
          >
            {busy === 'retry' ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Retrying…
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4" /> Retry from failed step
              </>
            )}
          </Button>
        </div>
      )}
      {toastError && (
        <div className="rounded-md border border-negative/40 bg-negative/10 p-2 text-xs text-negative">
          {toastError}
        </div>
      )}
    </div>
  );
}

function AllocationCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border bg-background p-3">
      <p className="text-xs text-muted-foreground uppercase tracking-wide">{label}</p>
      <p className="text-sm font-semibold tabular-nums text-foreground mt-1">
        {value}
      </p>
    </div>
  );
}

function StepRow({
  idx,
  label,
  entry,
  isCurrent,
}: {
  idx: number;
  name: string;
  label: string;
  entry: DeploymentStepLogEntry | undefined;
  isCurrent: boolean;
}) {
  let Icon = Circle;
  let iconColor = 'text-muted-foreground';
  let statusText: string | null = null;

  if (entry?.status === 'done') {
    Icon = CheckCircle2;
    iconColor = 'text-positive';
  } else if (entry?.status === 'failed') {
    Icon = XCircle;
    iconColor = 'text-negative';
    statusText = 'failed';
  } else if (isCurrent) {
    Icon = Loader2;
    iconColor = 'text-primary';
  }

  return (
    <li
      className={cn(
        'flex items-start gap-3 p-2 rounded-md',
        isCurrent && 'bg-primary/5',
        entry?.status === 'failed' && 'bg-negative/5',
      )}
    >
      <Icon
        className={cn(
          'h-4 w-4 mt-0.5 shrink-0',
          iconColor,
          isCurrent && 'animate-spin',
        )}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs font-medium text-foreground">
            <span className="text-muted-foreground">{idx + 1}. </span>
            {label}
          </span>
          {entry?.txHash && (
            <a
              href={`https://basescan.org/tx/${entry.txHash}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-primary hover:underline inline-flex items-center gap-1 font-mono"
              title={entry.txHash}
            >
              {entry.txHash.slice(0, 6)}…{entry.txHash.slice(-4)}
              <ExternalLink className="h-3 w-3" />
            </a>
          )}
          {statusText && (
            <span className="text-xs text-negative font-medium">
              {statusText}
            </span>
          )}
        </div>
        {entry?.detail && (
          <p className="text-xs text-muted-foreground mt-0.5 break-words">
            {entry.detail}
          </p>
        )}
        {entry?.error && (
          <p className="text-xs text-negative mt-0.5 font-mono break-all">
            {entry.error}
          </p>
        )}
      </div>
    </li>
  );
}

function ModeBadge({
  mode,
  status,
}: {
  mode: 'dry_run' | 'execute';
  status: string;
}) {
  if (mode === 'dry_run') {
    return (
      <span className="text-xs px-2 py-1 rounded-md border border-muted-foreground/30 bg-muted-foreground/5 text-muted-foreground">
        DRY RUN
      </span>
    );
  }
  return (
    <span className="text-xs px-2 py-1 rounded-md border border-negative/30 bg-negative/5 text-negative inline-flex items-center gap-1">
      <AlertCircle className="h-3 w-3" /> LIVE — {status}
    </span>
  );
}
